import { useQuery } from "@apollo/client";
import { parseError } from "apollo";
import { ODOO_COMPANY_IDS } from "AppSettings";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import NavigationBar from "components/shared/NavigationBar";
import { useModals } from "ModalProvider";
import { OdooContext } from "OdooProvider";
import { useContext, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import exportProductsXlsx from "./exportProductsXlsx";
import {
  FETCH_STOCK_SUPPLIER_OVERVIEW,
  FETCH_SUPPLIER_CHINA_STOCK_PRODUCTS,
} from "./graphql";
import ProductPlansView from "./ProductPlansView";

const toNumber = (value) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const getCombinedPlans = (row) => {
  const confirmed = Array.isArray(row.confirmed_plans)
    ? row.confirmed_plans.map((plan) => ({ ...plan }))
    : [];
  const notConfirmed = Array.isArray(row.not_confirmed_plans)
    ? row.not_confirmed_plans.map((plan) => ({
        ...plan,
      }))
    : [];
  return [...confirmed, ...notConfirmed];
};

const SupplierStocksPage = () => {
  const { odooUrl } = useContext(OdooContext);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const modal = useModals();
  const { supplierId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({ productQ: "" });
  const params = Object.fromEntries(searchParams.entries());
  const productQ = params.productQ || "";
  const status = params.status === "negativeFcst" ? "negativeFcst" : "all";
  const { loading, error, data } = useQuery(
    FETCH_SUPPLIER_CHINA_STOCK_PRODUCTS,
    {
      variables: { supplierId },
      fetchPolicy:
        process.env.NODE_ENV === "development" ? "cache-first" : "network-only",
    },
  );
  const fetchOverview = useQuery(FETCH_STOCK_SUPPLIER_OVERVIEW, {
    variables: { supplierId },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={parseError(error)} />;

  const products = data.products.results;

  const overview = fetchOverview.data?.stockSupplierOverview
    ? JSON.parse(fetchOverview.data.stockSupplierOverview)
    : {};

  const rows = products.map((i) => {
    const item = overview[i.id] || {};
    return { ...i, ...item };
  });

  console.log("rows", rows);

  const normalizedQ = productQ.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    const matchedSearch =
      !normalizedQ ||
      String(row.number || "")
        .toLowerCase()
        .includes(normalizedQ) ||
      String(row.name || "")
        .toLowerCase()
        .includes(normalizedQ);
    if (!matchedSearch) return false;

    if (status === "negativeFcst") {
      return toNumber(row.fcst_qty) < 0;
    }

    return true;
  });
  const isAllFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedIds.includes(row.id));

  const updateSearchParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const openPlansModal = (row) => {
    const plans = getCombinedPlans(row);
    if (plans.length === 0) return;

    modal.present({
      title: row.number,
      subtitle: row.name,
      maxWidth: "max-w-4xl",
      children: <ProductPlansView plans={plans} />,
    });
  };

  async function exportSelectedProducts() {
    setExporting(true);
    setExportError(null);
    try {
      await exportProductsXlsx(
        data.supplier.nickName || supplierId,
        rows.filter((row) => selectedIds.includes(row.id)),
      );
    } catch (error) {
      setExportError(error);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <NavigationBar
        title={`Stock Overview: ${data.supplier.nickName}`}
        backTo="./.."
        rightButtons={
          <div className="flex items-center space-x-6">
            {fetchOverview.loading && (
              <InlineSpinner size={16} text="Loading stock data..." />
            )}
            {fetchOverview.error && <Errors error={fetchOverview.error} />}
          </div>
        }
      />

      <div className="p-6 flex-1 overflow-auto flex flex-col space-y-4">
        {exportError && <Errors error={exportError} />}
        <div className="flex items-center space-x-4">
          <Select
            value={status}
            onChange={(e) =>
              updateSearchParam(
                "status",
                e.target.value === "all" ? "" : e.target.value,
              )
            }
          >
            <option value="all">All Products</option>
            <option value="negativeFcst">Negative FCST</option>
          </Select>

          <div className="flex-1">
            <SearchBar
              value={productQ}
              placeholder="Search by product number or name"
              onChange={(nextQ) => updateSearchParam("productQ", nextQ)}
            />
          </div>
        </div>

        <div className="card p-0 max-h-full overflow-auto">
          <div className="h-full overflow-auto relative">
            <table className="with-line font-code">
              <thead className="whitespace-nowrap">
                <tr className="sticky top-0 backdrop-blur-2xl z-10 bg-gray-100/50 dark:bg-gray-800/50 backdrop-opacity-95">
                  <th className="px-4 py-3 align-middle">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="opacity-70"
                        checked={isAllFilteredSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(filteredRows.map((row) => row.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                      {selectedIds.length > 0 ? (
                        <div className="flex items-center space-x-3">
                          <div>{selectedIds.length} product(s) selected</div>
                          <Button onClick={() => setSelectedIds([])}>
                            Clear
                          </Button>

                          {overview && Object.keys(overview).length > 0 && (
                            <Button
                              onClick={exportSelectedProducts}
                              loading={exporting}
                              disabled={
                                fetchOverview.loading || !!fetchOverview.error
                              }
                            >
                              Export
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div>Product ({filteredRows.length})</div>
                      )}
                    </div>
                  </th>
                  <th></th>
                  <th className="px-4 text-right align-middle">Odoo AB</th>
                  <th className="px-4 text-right align-middle">Odoo LTD</th>
                  <th className="px-4 text-right align-middle">
                    Supplier Stock
                  </th>
                  <th className="px-4 text-right align-middle">Plans</th>
                  <th className="px-4 text-right align-middle">FCST</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const plans = getCombinedPlans(row);
                  const hasPlanQty =
                    !fetchOverview.loading &&
                    (row.planning_qty !== 0 || row.not_confirmed_qty !== 0);
                  const mergedQty =
                    toNumber(row.planning_qty) +
                    toNumber(row.not_confirmed_qty);
                  const isNegativeFcst = toNumber(row.fcst_qty) < 0;
                  return (
                    <tr
                      key={row.id}
                      className=" hover:bg-sky-50 dark:hover:bg-sky-900/50"
                    >
                      <td className="px-4 align-middle">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            className="opacity-70"
                            checked={selectedIds.includes(row.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds([...selectedIds, row.id]);
                              } else {
                                setSelectedIds(
                                  selectedIds.filter(
                                    (selectedId) => selectedId !== row.id,
                                  ),
                                );
                              }
                            }}
                          />
                          <Link to={`${row.id}`} target="_blank">
                            [{row.number}] {row.name}
                          </Link>
                        </div>
                      </td>
                      <td className="px-4 text-right align-middle">
                        <a
                          target="_blank"
                          href={`${odooUrl}/odoo/action-563/${row.odooId}/action-1061${
                            row.company !== "UNSET"
                              ? `?cids=${ODOO_COMPANY_IDS[row.company]}`
                              : ""
                          }`}
                          className="text-purple-600"
                        >
                          Open in Odoo
                        </a>
                      </td>
                      <td className="px-4 text-right align-middle">
                        {row.odoo_fcst ?? "-"}
                      </td>
                      <td className="px-4 text-right align-middle">
                        {row.odoo_ltd_fcst ?? "-"}
                      </td>
                      <td className="px-4 text-right align-middle">
                        {row.ss ?? "-"}
                      </td>
                      <td className="px-4 text-right align-middle">
                        <div className="flex justify-end">
                          {hasPlanQty ? (
                            <Button onClick={() => openPlansModal(row)}>
                              {mergedQty}({plans.length})
                            </Button>
                          ) : (
                            "-"
                          )}
                        </div>
                      </td>
                      <td className="px-4 text-right align-middle">
                        <span
                          className={
                            isNegativeFcst
                              ? "text-red-500 dark:text-red-400"
                              : ""
                          }
                        >
                          {row.fcst_qty ?? "-"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierStocksPage;
